import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { 
  ArrowLeft, 
  Sparkles, 
  Send, 
  RefreshCw, 
  BookOpen, 
  PenTool,
  MessageCircle,
  Lightbulb,
  CheckCircle2,
  XCircle,
  Target,
  TrendingUp,
  Loader2,
  ChevronDown,
  ChevronUp,
  Compass,
  Search,
  Palette,
  Megaphone,
  Heart,
  Landmark,
  Rocket,
  Home,
  X,
  LogOut
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Icon mapping for categories
const categoryIcons = {
  compass: Compass,
  search: Search,
  palette: Palette,
  megaphone: Megaphone,
  heart: Heart,
  sparkles: Sparkles,
  landmark: Landmark,
  rocket: Rocket
};

// Color mapping for categories
const categoryColors = {
  adventure: 'bg-blue-500',
  mystery: 'bg-purple-500',
  descriptive: 'bg-green-500',
  persuasive: 'bg-orange-500',
  emotional: 'bg-pink-500',
  fantasy: 'bg-violet-500',
  historical: 'bg-amber-500',
  sci_fi: 'bg-cyan-500'
};

const WritingWhiteboard = () => {
  const navigate = useNavigate();
  const chatEndRef = useRef(null);
  
  // State
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [currentPrompt, setCurrentPrompt] = useState(null);
  const [writingContent, setWritingContent] = useState('');
  const [wordCount, setWordCount] = useState(0);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analysis, setAnalysis] = useState(null);
  const [showAnalysis, setShowAnalysis] = useState(false);
  
  // Tutor chat state
  const [showTutor, setShowTutor] = useState(false);
  const [tutorMessages, setTutorMessages] = useState([]);
  const [tutorInput, setTutorInput] = useState('');
  const [isSendingMessage, setIsSendingMessage] = useState(false);
  const [sessionId, setSessionId] = useState(() => `session-${Date.now()}`);
  
  // Loading states
  const [loadingCategories, setLoadingCategories] = useState(true);
  const [loadingPrompt, setLoadingPrompt] = useState(false);

  // Fetch categories on mount
  useEffect(() => {
    fetchCategories();
  }, []);

  // Update word count
  useEffect(() => {
    const words = writingContent.trim().split(/\s+/).filter(w => w.length > 0);
    setWordCount(words.length);
  }, [writingContent]);

  // Scroll chat to bottom
  useEffect(() => {
    if (chatEndRef.current) {
      chatEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [tutorMessages]);

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/writing-coach/prompts/categories`);
      setCategories(response.data.categories);
    } catch (error) {
      toast.error('Failed to load writing categories');
      console.error(error);
    } finally {
      setLoadingCategories(false);
    }
  };

  const fetchRandomPrompt = async (categoryId = null) => {
    setLoadingPrompt(true);
    try {
      const url = categoryId 
        ? `${API_URL}/api/writing-coach/prompts/random?category=${categoryId}`
        : `${API_URL}/api/writing-coach/prompts/random`;
      const response = await axios.get(url);
      setCurrentPrompt(response.data.prompt);
      setWritingContent('');
      setAnalysis(null);
      setShowAnalysis(false);
      setSelectedCategory(categoryId);
    } catch (error) {
      toast.error('Failed to load writing prompt');
      console.error(error);
    } finally {
      setLoadingPrompt(false);
    }
  };

  const analyzeWriting = async () => {
    if (wordCount < 20) {
      toast.error('Please write at least 20 words for meaningful feedback');
      return;
    }

    setIsAnalyzing(true);
    try {
      const response = await axios.post(`${API_URL}/api/writing-coach/analyze`, {
        prompt_id: currentPrompt.id,
        content: writingContent,
        session_id: sessionId
      });
      
      setAnalysis(response.data.analysis);
      setShowAnalysis(true);
      toast.success('Analysis complete! Check your feedback below.');
    } catch (error) {
      toast.error('Failed to analyze writing. Please try again.');
      console.error(error);
    } finally {
      setIsAnalyzing(false);
    }
  };

  const sendTutorMessage = async () => {
    if (!tutorInput.trim()) return;

    const userMessage = tutorInput.trim();
    setTutorInput('');
    setTutorMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    setIsSendingMessage(true);

    try {
      const response = await axios.post(`${API_URL}/api/writing-coach/tutor/chat`, {
        session_id: sessionId,
        message: userMessage,
        context: {
          writing_content: writingContent,
          prompt_title: currentPrompt?.title,
          prompt_text: currentPrompt?.prompt
        }
      });

      setTutorMessages(prev => [...prev, { role: 'assistant', content: response.data.response }]);
    } catch (error) {
      toast.error('Failed to send message. Please try again.');
      setTutorMessages(prev => [...prev, { 
        role: 'assistant', 
        content: "I'm sorry, I couldn't process that message. Please try again!" 
      }]);
    } finally {
      setIsSendingMessage(false);
    }
  };

  const quickTutorPrompts = [
    { text: "Give me a hint", icon: Lightbulb },
    { text: "How can I improve my opening?", icon: TrendingUp },
    { text: "What descriptive words could I use?", icon: Sparkles },
    { text: "Help me with my ending", icon: Target }
  ];

  const renderScoreBar = (score, maxScore = 10) => {
    const percentage = (score / maxScore) * 100;
    let color = 'bg-red-500';
    if (percentage >= 70) color = 'bg-green-500';
    else if (percentage >= 50) color = 'bg-yellow-500';
    else if (percentage >= 30) color = 'bg-orange-500';
    
    return (
      <div className="w-full bg-gray-700 rounded-full h-2">
        <div 
          className={`${color} h-2 rounded-full transition-all duration-500`}
          style={{ width: `${percentage}%` }}
        />
      </div>
    );
  };

  // Category selection view
  if (!currentPrompt) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="flex items-center justify-between mb-8">
            <button 
              onClick={() => navigate('/welcome')}
              className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
              Back to Dashboard
            </button>
            <button 
              onClick={() => {
                localStorage.clear();
                window.location.replace('/');
              }}
              className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors"
              data-testid="logout-btn"
            >
              <LogOut className="w-5 h-5" />
              Logout
            </button>
          </div>

          {/* Title */}
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-3 mb-4">
              <div className="p-3 bg-gradient-to-r from-amber-500 to-orange-500 rounded-xl shadow-lg">
                <PenTool className="w-8 h-8 text-white" />
              </div>
              <h1 className="text-4xl font-bold text-gray-900">AI Writing Coach</h1>
            </div>
            <p className="text-gray-600 text-lg max-w-2xl mx-auto">
              Choose a writing category and let our AI tutor guide you through creative writing with instant feedback and personalized tips.
            </p>
          </div>

          {/* Categories Grid */}
          {loadingCategories ? (
            <div className="flex justify-center py-20">
              <Loader2 className="w-8 h-8 animate-spin text-amber-500" />
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {categories.map((category) => {
                const IconComponent = categoryIcons[category.icon] || BookOpen;
                const bgColor = categoryColors[category.id] || 'bg-gray-500';
                
                return (
                  <Card 
                    key={category.id}
                    className="bg-white border-gray-200 hover:border-amber-400 hover:shadow-lg transition-all cursor-pointer group"
                    onClick={() => fetchRandomPrompt(category.id)}
                    data-testid={`category-${category.id}`}
                  >
                    <CardContent className="p-6">
                      <div className={`w-12 h-12 ${bgColor} rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform shadow`}>
                        <IconComponent className="w-6 h-6 text-white" />
                      </div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-1">{category.name}</h3>
                      <p className="text-gray-600 text-sm mb-3">{category.description}</p>
                      <Badge variant="secondary" className="bg-gray-100 text-gray-700 border border-gray-200">
                        {category.count} prompts
                      </Badge>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}

          {/* Random Prompt Button */}
          <div className="text-center mt-8">
            <Button
              onClick={() => fetchRandomPrompt()}
              className="bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white shadow-lg"
              data-testid="random-prompt-btn"
            >
              <Sparkles className="w-4 h-4 mr-2" />
              Surprise Me! (Random Prompt)
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Writing view
  return (
    <div className="min-h-screen bg-gray-50">
      <div className="flex h-screen">
        {/* Main Writing Area */}
        <div className={`flex-1 flex flex-col ${showTutor ? 'mr-96' : ''} transition-all duration-300`}>
          {/* Header */}
          <div className="p-4 border-b border-gray-200 bg-white flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button 
                onClick={() => setCurrentPrompt(null)}
                className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors"
              >
                <ArrowLeft className="w-5 h-5" />
                Back
              </button>
              <div className="h-6 w-px bg-gray-300" />
              <Badge className={`${categoryColors[currentPrompt.category]} text-white`}>
                {currentPrompt.category?.replace('_', ' ')}
              </Badge>
            </div>
            
            <div className="flex items-center gap-4">
              <Button
                variant="outline"
                size="sm"
                onClick={() => fetchRandomPrompt(selectedCategory)}
                className="border-gray-300 text-gray-700 hover:bg-gray-100"
                data-testid="new-prompt-btn"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                New Prompt
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowTutor(!showTutor)}
                className={`border-gray-300 ${showTutor ? 'bg-amber-500 text-white border-amber-500' : 'text-gray-700 hover:bg-gray-100'}`}
                data-testid="toggle-tutor-btn"
              >
                <MessageCircle className="w-4 h-4 mr-2" />
                AI Tutor
              </Button>
            </div>
          </div>

          {/* Prompt Display */}
          <div className="p-6 border-b border-gray-200 bg-amber-50">
            <h2 className="text-2xl font-bold text-gray-900 mb-2">{currentPrompt.title}</h2>
            <p className="text-gray-700 leading-relaxed">{currentPrompt.prompt}</p>
            
            {/* Hints */}
            {currentPrompt.hints && currentPrompt.hints.length > 0 && (
              <div className="mt-4 flex flex-wrap gap-2">
                <span className="text-gray-500 text-sm">Hints:</span>
                {currentPrompt.hints.map((hint, i) => (
                  <Badge key={i} variant="outline" className="border-amber-300 text-amber-700 bg-amber-100">
                    <Lightbulb className="w-3 h-3 mr-1" />
                    {hint}
                  </Badge>
                ))}
              </div>
            )}
            
            {/* Word target */}
            <div className="mt-4 flex items-center gap-4">
              <span className="text-gray-500 text-sm">
                Target: {currentPrompt.word_target} words
              </span>
              <span className="text-gray-400 text-sm">|</span>
              <span className={`text-sm ${wordCount >= currentPrompt.word_target ? 'text-green-600 font-medium' : 'text-gray-600'}`}>
                Current: {wordCount} words
              </span>
              <div className="flex-1 max-w-xs">
                <Progress 
                  value={Math.min((wordCount / currentPrompt.word_target) * 100, 100)} 
                  className="h-2"
                />
              </div>
            </div>
          </div>

          {/* Writing Area */}
          <div className="flex-1 p-6 overflow-auto bg-white">
            <Textarea
              value={writingContent}
              onChange={(e) => setWritingContent(e.target.value)}
              placeholder="Start writing your story here... Let your imagination flow!"
              className="w-full h-full min-h-[400px] bg-white border-gray-300 text-gray-900 placeholder-gray-400 resize-none focus:border-amber-500 focus:ring-amber-500 text-lg leading-relaxed"
              data-testid="writing-textarea"
            />
          </div>

          {/* Action Bar */}
          <div className="p-4 border-t border-gray-200 bg-white">
            <div className="flex items-center justify-between">
              <div className="text-gray-600 text-sm">
                {wordCount} / {currentPrompt.word_target} words
              </div>
              <Button
                onClick={analyzeWriting}
                disabled={isAnalyzing || wordCount < 20}
                className="bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white shadow"
                data-testid="analyze-btn"
              >
                {isAnalyzing ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Analyzing...
                  </>
                ) : (
                  <>
                    <Sparkles className="w-4 h-4 mr-2" />
                    Submit and Get Instant Feedback
                  </>
                )}
              </Button>
            </div>
          </div>

          {/* Analysis Results */}
          {showAnalysis && analysis && (
            <div className="border-t border-gray-200 bg-white p-6 max-h-[50vh] overflow-auto">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                  <Sparkles className="w-5 h-5 text-amber-500" />
                  AI Writing Feedback
                </h3>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowAnalysis(false)}
                  className="text-gray-500 hover:text-gray-900"
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>

              {/* Overall Score */}
              <div className="mb-6 text-center">
                <div className="inline-flex items-center justify-center w-24 h-24 rounded-full bg-gradient-to-r from-amber-500 to-orange-500 mb-2 shadow-lg">
                  <span className="text-3xl font-bold text-white">{analysis.overall_score}</span>
                </div>
                <p className="text-gray-600">Overall Score</p>
              </div>

              {/* Score Breakdown */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
                {[
                  { key: 'vocabulary_richness', label: 'Vocabulary', icon: BookOpen },
                  { key: 'sentence_variation', label: 'Sentences', icon: PenTool },
                  { key: 'structure', label: 'Structure', icon: Target },
                  { key: 'grammar', label: 'Grammar', icon: CheckCircle2 },
                  { key: 'creativity', label: 'Creativity', icon: Sparkles }
                ].map(({ key, label, icon: Icon }) => (
                  <Card key={key} className="bg-gray-50 border-gray-200">
                    <CardContent className="p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <Icon className="w-4 h-4 text-amber-500" />
                        <span className="text-sm text-gray-700">{label}</span>
                      </div>
                      <div className="text-2xl font-bold text-gray-900 mb-2">
                        {analysis[key]?.score || 0}/10
                      </div>
                      {renderScoreBar(analysis[key]?.score || 0)}
                      <p className="text-xs text-gray-600 mt-2 line-clamp-2">
                        {analysis[key]?.feedback}
                      </p>
                    </CardContent>
                  </Card>
                ))}
              </div>

              {/* Overall Feedback */}
              <Card className="bg-gray-50 border-gray-200 mb-4">
                <CardContent className="p-4">
                  <p className="text-gray-700 leading-relaxed">{analysis.overall_feedback}</p>
                </CardContent>
              </Card>

              {/* Strengths and Areas to Improve */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <Card className="bg-green-50 border-green-200">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-green-700 text-sm flex items-center gap-2">
                      <CheckCircle2 className="w-4 h-4" />
                      Strengths
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-1">
                      {analysis.strengths?.map((strength, i) => (
                        <li key={i} className="text-gray-700 text-sm flex items-start gap-2">
                          <span className="text-green-600 mt-1">•</span>
                          {strength}
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>

                <Card className="bg-amber-50 border-amber-200">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-amber-700 text-sm flex items-center gap-2">
                      <TrendingUp className="w-4 h-4" />
                      Areas to Improve
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-1">
                      {analysis.areas_to_improve?.map((area, i) => (
                        <li key={i} className="text-gray-700 text-sm flex items-start gap-2">
                          <span className="text-amber-400 mt-1">•</span>
                          {area}
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              </div>

              {/* Specific Suggestions */}
              <Card className="bg-purple-900/30 border-purple-700">
                <CardHeader className="pb-2">
                  <CardTitle className="text-purple-400 text-sm flex items-center gap-2">
                    <Lightbulb className="w-4 h-4" />
                    Suggestions to Try
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-1">
                    {analysis.specific_suggestions?.map((suggestion, i) => (
                      <li key={i} className="text-gray-700 text-sm flex items-start gap-2">
                        <span className="text-violet-600 mt-1">{i + 1}.</span>
                        {suggestion}
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            </div>
          )}
        </div>

        {/* AI Tutor Sidebar */}
        {showTutor && (
          <div className="fixed right-0 top-0 h-full w-96 bg-white border-l border-gray-200 flex flex-col z-50 shadow-xl">
            {/* Tutor Header */}
            <div className="p-4 border-b border-gray-200 flex items-center justify-between bg-gradient-to-r from-amber-50 to-orange-50">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-gradient-to-r from-amber-500 to-orange-500 rounded-full flex items-center justify-center shadow">
                  <Sparkles className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">Ed - AI Tutor</h3>
                  <p className="text-xs text-gray-500">Here to help you write!</p>
                </div>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowTutor(false)}
                className="text-gray-500 hover:text-gray-900"
              >
                <X className="w-4 h-4" />
              </Button>
            </div>

            {/* Quick Prompts */}
            <div className="p-3 border-b border-gray-200 flex flex-wrap gap-2 bg-gray-50">
              {quickTutorPrompts.map((prompt, i) => (
                <Button
                  key={i}
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setTutorInput(prompt.text);
                  }}
                  className="border-gray-300 text-gray-700 hover:bg-gray-100 text-xs"
                >
                  <prompt.icon className="w-3 h-3 mr-1" />
                  {prompt.text}
                </Button>
              ))}
            </div>

            {/* Chat Messages */}
            <div className="flex-1 overflow-auto p-4 space-y-4 bg-gray-50">
              {tutorMessages.length === 0 && (
                <div className="text-center text-gray-500 py-8">
                  <MessageCircle className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p className="text-gray-700">Hi! I'm Ed, your AI writing tutor.</p>
                  <p className="text-sm mt-1 text-gray-500">Ask me anything about your writing!</p>
                </div>
              )}
              
              {tutorMessages.map((msg, i) => (
                <div
                  key={i}
                  className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                >
                  <div
                    className={`max-w-[80%] p-3 rounded-lg shadow-sm ${
                      msg.role === 'user'
                        ? 'bg-amber-500 text-white'
                        : 'bg-white text-gray-800 border border-gray-200'
                    }`}
                  >
                    <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                  </div>
                </div>
              ))}
              
              {isSendingMessage && (
                <div className="flex justify-start">
                  <div className="bg-white border border-gray-200 p-3 rounded-lg shadow-sm">
                    <Loader2 className="w-4 h-4 animate-spin text-amber-500" />
                  </div>
                </div>
              )}
              
              <div ref={chatEndRef} />
            </div>

            {/* Chat Input */}
            <div className="p-4 border-t border-gray-200 bg-white">
              <div className="flex gap-2">
                <input
                  type="text"
                  value={tutorInput}
                  onChange={(e) => setTutorInput(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && sendTutorMessage()}
                  placeholder="Ask Ed anything..."
                  className="flex-1 bg-gray-50 border border-gray-300 rounded-lg px-3 py-2 text-gray-900 placeholder-gray-400 text-sm focus:border-amber-500 focus:outline-none focus:ring-1 focus:ring-amber-500"
                  data-testid="tutor-input"
                />
                <Button
                  onClick={sendTutorMessage}
                  disabled={!tutorInput.trim() || isSendingMessage}
                  className="bg-amber-500 hover:bg-amber-600 text-white"
                  data-testid="tutor-send-btn"
                >
                  <Send className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default WritingWhiteboard;
